/**
 * WHAT'S NEW — the active side of the changelog.
 *
 * The changelog (FEATURES.md, shown in Changelog.tsx) says WHAT changed. It does
 * not say WHERE: a person reads "a board can run as a service now" and is left to
 * hunt for the control. Vinnie, 10 October 2026: the What's New was meant to have
 * "an active call out to show users where the new features were."
 *
 * So the headline features of the current release carry a `target` — the same
 * drill the Help corpus (help.ts) and the project guide use — and the What's New
 * offers a "Show me" that takes the person to the real control and highlights it
 * (`drillTo` → `revealId`, picked up by CoachMark on arrival). The list is short
 * and curated on purpose: the three or four things worth a tour, not every line
 * in FEATURES.md.
 *
 * EXAMPLE-FIRST, LIKE THE HELP (design rule P9). Each `blurb` is a worked next
 * action — "Open X → do Y" — never "X is a feature that…". Each `target` points
 * at a control that actually exists, read off the live component.
 *
 * WHEN A FEATURE DOES NOT APPLY. Some highlights are project-only (a contract
 * value, a billable milestone mean nothing on a continuous service — #435). On a
 * board where the target screen is hidden, the item still reads so the person
 * knows the feature exists, but it carries no "Show me" that would land them on a
 * screen that is not there — `projectOnly` says so and the dialog decides.
 */
import type { View } from './store';

export interface WhatsNewItem {
  id: string;
  /** The feature, named the way a person would recognise it. */
  title: string;
  /** One example-first line: what it does and where it lives. */
  blurb: string;
  /** Where "Show me" takes them — a drill target, the control's own reveal id. */
  target?: { view: View; reveal?: string };
  /** True when the target only exists on a board run as a project (#435). */
  projectOnly?: boolean;
}

/**
 * The release these highlights belong to — the `major.minor`, without the patch,
 * so a 3.19.1 fast-follow does not re-announce 3.19 to someone who has seen it.
 * Bumped when a release has a tour worth showing; the seen-mark is keyed to it.
 */
export const WHATS_NEW_VERSION = '3.31';

/** A short, human label for the release — the sidebar callout's heading. */
export const WHATS_NEW_TITLE = "What's new in 3.31";

/**
 * The heading over the tour INSIDE the changelog. Deliberately NOT the same words
 * as WHATS_NEW_TITLE or FEATURES.md's own `## What's new in 3.19` section: on live
 * the changelog shows no "unreleased" block, so an identical panel heading would
 * sit right on top of that section heading (ux-witness, 10 Oct). This names what
 * the panel does instead — it is the active, "take me there" half.
 */
export const WHATS_NEW_TOUR_HEADING = 'New in 3.31 — where to find it';

export const WHATS_NEW: WhatsNewItem[] = [
  {
    id: 'working-calendar',
    title: 'A workspace working calendar',
    blurb:
      'In Settings, set your working week and your holidays once. From then on every date FlightDeck works out — the "when will it be done" forecast and the nudge for a card that has sat too long — counts working days and skips weekends and your holidays.',
    target: { view: 'settings' },
  },
  {
    id: 'milestone-forecast',
    title: 'Milestones forecast themselves',
    blurb:
      'Open a project’s Plan: each milestone now predicts its own finish from the board’s real pace, leading with the 85%-confident date — the one you can stand behind — and the 50/50 midpoint beside it.',
    target: { view: 'plan' },
    projectOnly: true,
  },
  {
    id: 'portfolio-status-drill',
    title: 'A drillable Portfolio status report',
    blurb:
      'In the Portfolio, open a project’s status report: it leads with the 85%-confident go-live, adds a schedule-and-cost (earned-value) and spare-capacity read, and every reference — Waiting On, an at-risk card, a Work in Flight chip — opens the card it names.',
    target: { view: 'portfolio' },
  },
];
